/**
 * ether5Grid - quilt container for <ether5-card>
 *
 *   <ether5-grid data-ether5-gap="12" data-ether5-cols="4">
 *     <ether5-card ...></ether5-card>
 *   </ether5-grid>
 *
 * WHY A QUILT AND NOT MASONRY
 *
 * Native masonry (`grid-template-rows: masonry`, being renamed "grid lanes")
 * ships in no engine, so this is a span quilt with `grid-auto-flow: dense` to
 * backfill holes.
 *
 * Dense packing pulls later items forward visually while DOM order stays put, so
 * visual order and sequential order diverge - which also means tab order and
 * screen-reader reading order follow the markup, not the layout. That is an
 * accepted trade here: keyboard traversal is not part of this grid's UX. Revisit
 * this line first if that ever changes, because it is the whole cause.
 *
 * THE GEOMETRY
 *
 * Rows are made exactly as tall as columns are wide:
 *
 *   row height = (container inline size - (cols - 1) * gap) / cols
 *
 * That one line is what makes the shape contract hold - a 2-column span is then
 * `2 * cell + gap` wide, and a 2-row span is `2 * cell + gap` tall, because the
 * spanned gap comes along with the tracks.
 *
 * It needs the container's own width at computed-value time, which is what
 * `100cqi` gives. A container cannot query itself, so `container-type` sits on
 * :host and the grid that reads `cqi` is a shadow descendant of it.
 *
 * Column count steps down by container query, not media query - the grid is
 * sized by whatever bubble or panel holds it, not by the viewport.
 */

const STYLES = `
  :host {
    container-type: inline-size;
    display: block;
    inline-size: 100%;

    --e5-gap: 24px;
    --e5-cols: 4;
    --e5-span-w: 2;   /* read by WIDE cards; drops to 1 in a single column */
  }

  .grid {
    display: grid;
    gap: var(--e5-gap);
    grid-template-columns: repeat(var(--e5-cols), minmax(0, 1fr));
    grid-auto-rows: calc(
      (100cqi - (var(--e5-cols) - 1) * var(--e5-gap)) / var(--e5-cols)
    );
    /* Backfill the holes a WIDE or TALL span leaves behind. Decouples visual
       order from DOM order - see the header. */
    grid-auto-flow: row dense;
  }

  @container (inline-size < 880px) { :host { --e5-cols: 3; } }
  @container (inline-size < 600px) { :host { --e5-cols: 2; } }
  @container (inline-size < 360px) { :host { --e5-cols: 1; --e5-span-w: 1; } }

  ::slotted(ether5-card) { min-inline-size: 0; }
`;

class Ether5Grid extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._built = false;
  }

  static get observedAttributes() {
    return ['data-ether5-gap', 'data-ether5-cols'];
  }

  connectedCallback() {
    this.build();
    this.paint();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'list');
  }

  attributeChangedCallback() {
    if (this._built) this.paint();
  }

  build() {
    if (this._built) return;
    this.shadowRoot.innerHTML = `<style>${STYLES}</style><div class="grid" part="grid"><slot></slot></div>`;
    this._built = true;
  }

  paint() {
    const gap = this.getAttribute('data-ether5-gap');
    const cols = this.getAttribute('data-ether5-cols');

    // Set on the host so the value inherits across the shadow boundary into each
    // card - custom properties are the only thing that crosses it.
    if (gap) this.style.setProperty('--e5-gap', /^\d+$/.test(gap) ? `${gap}px` : gap);

    // An explicit column count opts out of the responsive steps above; the
    // container queries still win at the narrow end because they target :host.
    if (cols) this.style.setProperty('--e5-cols', cols);
  }
}

customElements.define('ether5-grid', Ether5Grid);

export default Ether5Grid;
